/**
 * ConfirmDialog (Workspace v2 Slice 3b-2) — a small styled confirm, on-brand with
 * HandoffDialog / MoveStageDialog (replaces the native window.confirm soft-nag).
 *
 * UX-8: re-hosted on the Dialog primitive (gains Esc-close + focus trap +
 * restore-focus from useDialogDismiss) and the Button primitive. Testids
 * (confirm-dialog / confirm-body / confirm-ok / confirm-cancel) unchanged.
 */

import { type ReactNode } from 'react';
import { Dialog } from '@/components/primitives/Dialog';
import { Button } from '@/components/primitives/Button';

export interface ConfirmDialogProps {
  title: string;
  /** Optional: a confirm whose title says it all renders no body line. */
  body?: string;
  confirmLabel?: string;
  /** Destructive actions (e.g. Delete) get a pepper confirm button. */
  destructive?: boolean;
  /** A failed confirm's sentence (from errorCopy), shown inside the dialog. */
  error?: string | null;
  errorTestId?: string;
  /** While the confirmed action runs, both buttons are disabled. */
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmDialog({
  title,
  body,
  confirmLabel = 'Continue',
  destructive = false,
  error = null,
  errorTestId,
  busy = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps): ReactNode {
  return (
    <Dialog
      role="alertdialog"
      size="sm"
      title={title}
      onClose={onCancel}
      testId="confirm-dialog"
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onCancel} disabled={busy} data-testid="confirm-cancel">
            Cancel
          </Button>
          <Button
            variant={destructive ? 'danger' : 'primary'}
            size="sm"
            onClick={onConfirm}
            disabled={busy}
            data-testid="confirm-ok"
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      {body ? (
        <p className="text-[13px] text-ink-soft mb-4" data-testid="confirm-body">
          {body}
        </p>
      ) : (
        <div className="mb-4" />
      )}
      {error ? (
        <p
          role="alert"
          className="text-sm text-warm-700 bg-warm-50 border border-warm-200 rounded-base px-3 py-2 mb-3"
          data-testid={errorTestId}
        >
          {error}
        </p>
      ) : null}
    </Dialog>
  );
}
